import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useCookieConsent } from '../../../../context/CookieConsentContext';
import { useLanguage } from '../../../../context/LanguageContext';
import { useTheme } from '../../../../context/ThemeContext';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';

const CookiesPage: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const { cookiePreferences, acceptAllCookies, customizeCookies } = useCookieConsent();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  const openPreferences = () => {
    // Show a message to the user
    alert(language === 'en' 
      ? 'Please use the cookie banner at the bottom of the page to manage your preferences.' 
      : 'Por favor, use o banner de cookies na parte inferior da página para gerenciar suas preferências.');
    
    // Scroll to bottom where the banner would be
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <SEO 
        title={seoData.cookies.title}
        description={seoData.cookies.description}
        canonical={seoData.cookies.canonical}
      />
      {/* Hero Section */}
      <section 
        className="cookies-hero d-flex align-items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1599508704512-2f19efd1e35f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Y29va2llc3xlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=1200&q=80)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {language === 'en' ? 'Cookie Policy' : 'Política de Cookies'}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Policy Content */}
      <section className="cookie-policy-content py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className={`bg-${theme === 'dark' ? 'dark text-light' : 'white'} p-4 p-md-5 rounded shadow`}>
                <div className="mb-5" data-aos="fade-up">
                  <h2 className="h3 mb-4">{language === 'en' ? 'About Our Cookies' : 'Sobre Nossos Cookies'}</h2>
                  <p>
                    {language === 'en' 
                      ? 'This website uses cookies to improve your browsing experience and provide personalized services. Cookies are small text files that are stored on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to the website owners.'
                      : 'Este site utiliza cookies para melhorar sua experiência de navegação e fornecer serviços personalizados. Cookies são pequenos arquivos de texto que são armazenados em seu dispositivo quando você visita um site. Eles são amplamente utilizados para fazer com que os sites funcionem de maneira mais eficiente e fornecer informações aos proprietários do site.'}
                  </p>
                </div>
                
                <div className="mb-5" data-aos="fade-up" data-aos-delay="100">
                  <h3 className="h4 mb-3">{language === 'en' ? 'Types of Cookies We Use' : 'Tipos de Cookies que Utilizamos'}</h3>
                  
                  <div className={`p-4 mb-4 rounded ${theme === 'dark' ? 'bg-secondary' : 'bg-light'}`} style={{ 
                    backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
                  }}>
                    <h4 className="h5 mb-3">
                      <i className="bi bi-shield-lock text-primary me-2"></i>
                      {language === 'en' ? 'Necessary Cookies' : 'Cookies Necessários'}
                    </h4>
                    <p className="mb-0">
                      {language === 'en' 
                        ? 'These cookies are essential for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.'
                        : 'Estes cookies são essenciais para o funcionamento adequado do site. Eles permitem funções básicas como navegação de páginas e acesso a áreas seguras do site. O site não pode funcionar corretamente sem estes cookies.'}
                    </p>
                  </div>
                  
                  <div className={`p-4 mb-4 rounded ${theme === 'dark' ? 'bg-secondary' : 'bg-light'}`} style={{ 
                    backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
                  }}>
                    <h4 className="h5 mb-3">
                      <i className="bi bi-translate text-primary me-2"></i>
                      {language === 'en' ? 'Language Preference Cookies' : 'Cookies de Preferência de Idioma'}
                    </h4>
                    <p className="mb-0">
                      {language === 'en' 
                        ? 'These cookies remember your language preference. When you select a language, we store this preference so that our website appears in your chosen language when you return.'
                        : 'Estes cookies lembram sua preferência de idioma. Quando você seleciona um idioma, armazenamos essa preferência para que nosso site apareça no idioma escolhido quando você retornar.'}
                    </p>
                  </div>
                  
                  <div className={`p-4 mb-4 rounded ${theme === 'dark' ? 'bg-secondary' : 'bg-light'}`} style={{ 
                    backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
                  }}>
                    <h4 className="h5 mb-3">
                      <i className="bi bi-palette text-primary me-2"></i>
                      {language === 'en' ? 'Theme Preference Cookies' : 'Cookies de Preferência de Tema'}
                    </h4>
                    <p className="mb-0">
                      {language === 'en' 
                        ? 'These cookies remember your theme preference (light or dark mode). When you select a theme, we store this preference so that our website appears with your chosen theme when you return.'
                        : 'Estes cookies lembram sua preferência de tema (modo claro ou escuro). Quando você seleciona um tema, armazenamos essa preferência para que nosso site apareça com o tema escolhido quando você retornar.'}
                    </p>
                  </div>
                </div>
                
                <div className="mb-5" data-aos="fade-up" data-aos-delay="200">
                  <h3 className="h4 mb-3">{language === 'en' ? 'Managing Your Cookie Preferences' : 'Gerenciando suas Preferências de Cookies'}</h3>
                  <p>
                    {language === 'en' 
                      ? 'You can manage your cookie preferences at any time. You can choose to accept or reject non-essential cookies. Note that rejecting some cookies may impact your experience on our website.'
                      : 'Você pode gerenciar suas preferências de cookies a qualquer momento. Você pode optar por aceitar ou rejeitar cookies não essenciais. Observe que rejeitar alguns cookies pode afetar sua experiência em nosso site.'}
                  </p>
                  
                  <div className="d-flex flex-wrap gap-3 mt-4">
                    <button 
                      className="btn btn-primary rounded-pill px-4" 
                      onClick={openPreferences}
                    >
                      <i className="bi bi-sliders me-2"></i>
                      {language === 'en' ? 'Manage Cookie Preferences' : 'Gerenciar Preferências de Cookies'}
                    </button>
                    
                    <button 
                      className="btn btn-outline-primary rounded-pill px-4" 
                      onClick={acceptAllCookies}
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      {language === 'en' ? 'Accept All Cookies' : 'Aceitar Todos os Cookies'}
                    </button>
                  </div>
                </div>
                
                <div className="mb-5" data-aos="fade-up" data-aos-delay="300">
                  <h3 className="h4 mb-3">{language === 'en' ? 'Your Current Cookie Settings' : 'Suas Configurações Atuais de Cookies'}</h3>
                  
                  <div className={`table-responsive rounded ${theme === 'dark' ? 'bg-secondary' : 'bg-light'}`} style={{ 
                    backgroundColor: theme === 'dark' ? '#2a2c2e' : '' 
                  }}>
                    <table className={`table ${theme === 'dark' ? 'table-dark' : ''} mb-0`}>
                      <thead>
                        <tr>
                          <th>{language === 'en' ? 'Cookie Type' : 'Tipo de Cookie'}</th>
                          <th>{language === 'en' ? 'Status' : 'Status'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{language === 'en' ? 'Necessary Cookies' : 'Cookies Necessários'}</td>
                          <td>
                            <span className="badge bg-success">
                              {language === 'en' ? 'Always Active' : 'Sempre Ativo'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>{language === 'en' ? 'Language Preference' : 'Preferência de Idioma'}</td>
                          <td>
                            {cookiePreferences.language ? (
                              <span className="badge bg-success">
                                {language === 'en' ? 'Enabled' : 'Ativado'}
                              </span>
                            ) : (
                              <span className="badge bg-danger">
                                {language === 'en' ? 'Disabled' : 'Desativado'}
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>{language === 'en' ? 'Theme Preference' : 'Preferência de Tema'}</td>
                          <td>
                            {cookiePreferences.theme ? (
                              <span className="badge bg-success">
                                {language === 'en' ? 'Enabled' : 'Ativado'}
                              </span>
                            ) : (
                              <span className="badge bg-danger">
                                {language === 'en' ? 'Disabled' : 'Desativado'}
                              </span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div data-aos="fade-up" data-aos-delay="400">
                  <h3 className="h4 mb-3">{language === 'en' ? 'More Information' : 'Mais Informações'}</h3>
                  <p>
                    {language === 'en' 
                      ? 'For more information about how we use cookies and your personal data, please refer to our Privacy Policy.'
                      : 'Para mais informações sobre como usamos cookies e seus dados pessoais, consulte nossa Política de Privacidade.'}
                  </p>
                  <p className="mb-0">
                    {language === 'en'
                      ? 'If you have any questions about our use of cookies, please contact us.'
                      : 'Se você tiver alguma dúvida sobre nosso uso de cookies, entre em contato conosco.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CookiesPage; 